import { I18n } from 'react-redux-i18n';
import isEmpty from 'lodash/isEmpty';
import { toCanvas } from 'html-to-image';
import print from 'print-js'
import debounce from 'lodash/debounce';
import isNil from 'lodash/isNil';
import mergeImages from 'merge-images';

import { downloadFile } from '@common/utils/file';
import { SORT_DIRECTION } from '@common/constants';
import { Table, scrollTop, getHeader, getBody } from '@common/components/Table';
import { Flex } from '@common/components/Flex';
import { getTestAttributes } from '@common/utils/tests';
import { Button } from '@common/components/Button';
import { MessageBox } from '@common/components/MessageBox';
import { getReport, getLastFilters, setColumnsWidth, exportData } from './actions/main';
import { NewReport } from './components/new-report';
import { formatDate, parseDate } from '@common/utils/date';
import { DataExport } from './components/data-export';
import { CONTEXT_TYPE } from './constants/main';
import { convertBoolToNumber } from '@common/utils/boolean';
import { FieldsEditing } from './components/fields-editing';

import './audit-report.styles.scss';

export const MODULE_NAME = 'Field-audit-report';
export const DATE_TYPE = {
  start: 'start',
  end: 'end'
};
const setupIcon = 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABEAAAARCAIAAAC0D9CtAAAAAXNSR0IArs4c6QAAAARnQU1BAACxjwv8YQUAAAAJcEhZcwAADsMAAA7DAcdvqGQAAAHxSURBVDhPY/hAOgDp2Xn2UdPqq2iocdWV6qUXyxacLZx9KmfasbSJhxJ79i/bfRWqB6jiPyr49///rz//vv38++n73/df/rz++Pv5+1+P3/wKb9qBogdoDH50/9XPoNqt6HqW3/y7/MafZdfREVAQKHvn+Q/fio3oelbc+td19nPXGXQE0XPj6Q+PknXoevCjq4+/O+evRtHz5++/H7/+ffnx98PXP28+/X7x4feTt78evv5598WPm09/ADVcfPDNLms5Qg9QA5qpWzZv2rZlE5CEo7P3vlmmLoHqqVtx+edvkB5IGAB9BWQf2LH575/fP2Fg08YNp25/NUlcCNVTufj81x9/geqAQZS+4zVQJ5B9cOfmP79+3rx1G4hAejZsOHbzi37MPKieknlnPn4DhQxQT92R98tvgPXsAOl58vQZEIH1rD90/bNW+CyonoKZJ999BukBqp5x5duKmyC37d++6e/v3x8/foIgoJ4DVz+rBk2H6smeevTVx99AdcgI6OnNGzcAvQFBQC5Qj4LfFKietAmHIMkJmDpuP/9x/cn3y4++n7//7fTdr8dvfTl8/QtQNQTJeE6A6lm570Z02+6wxh2BNVt8yje6Fa11zF1pk7HMLHmRYdx8ncjZ6iEzFP2nyHhNbJ61H6qHNPDhAwCYYZ3w8AQhfQAAAABJRU5ErkJggg==';
const defaultLoadMetadata = {
  pagination: {
    start: 0,
    size: 40
  }, 
  sorting: {},
};

export const CONTEXT_TYPE_ALIAS_CONFIG = {
  [CONTEXT_TYPE.individual]: 'individual',
  [CONTEXT_TYPE.pedigree]: 'pedigree',
};

const filtersDateFormat = 'MM/DD/YYYY';
const filtersDateInputFormat = 'YYYY-MM-DD';

const formatToFilterDateValue = (date) => {
  return formatDate(parseDate(date, filtersDateInputFormat), { format: filtersDateFormat });
};

const formatToFilterDateInput = (date) => {
  return formatDate(parseDate(date, filtersDateFormat), { format: filtersDateInputFormat });
};

export class AuditReport extends React.Component {
  rootRef = React.createRef();

  state = {
    messageProps: {
      header: '',
      text: '',
      show: false,
      confirmText: 'Ok',
      cancelText: '',
      size: 'medium',
      onCustomOk: () => {},
    },
    loadMetadata: defaultLoadMetadata,
    columnsWidth: undefined,
    items: [],
    totalItemsCount: 0,
    newReportConfigOpened: false,
    fieldsEditingOpened: false,
    defaultFiltersInitialized: false,
    filtering: {
      dates: { 
        [DATE_TYPE.start]: { use: false, value: '' }, 
        [DATE_TYPE.end]: { use: false, value: '' } 
      },
      users: [],
      updates: {
        [CONTEXT_TYPE_ALIAS_CONFIG[CONTEXT_TYPE.individual]]: {
          use: true,
          value: undefined,
        },
        [CONTEXT_TYPE_ALIAS_CONFIG[CONTEXT_TYPE.pedigree]]: {
          use: false,
          value: undefined,
        }
      }
    },
    dataExportOpened: false,
    printing: false,
  };

  componentDidMount() {
    this.initDefaultFiltersValue();
  }
  
  initDefaultFiltersValue = () => {
    getLastFilters().then((lastParams) => {
      const initialColumnIndex = 0;

      this.setState((prevState) => ({
        ...prevState,
        loadMetadata: {
          ...prevState.loadMetadata,
          pagination: {
            ...prevState.loadMetadata.pagination,
            size: lastParams.size || defaultLoadMetadata.pagination.size
          },
          sorting: {
            direction: lastParams.sortAsc === 'ASC' ? SORT_DIRECTION.asc : SORT_DIRECTION.desc,
            columnIndex: isNil(lastParams.sortColIndex) 
              ? initialColumnIndex 
              : lastParams.sortColIndex
          }
        },
        defaultFiltersInitialized: true,
        filtering: {
          updates: {
            individual: {
              use: lastParams.indFlag,
              value: {
                fieldId: lastParams.indItemId,
                fieldName: lastParams.indItemName,
                imageURL: lastParams.indItemImageURL,
                systemField: lastParams.indItemSystem
              },
            },
            pedigree: {
              use: lastParams.pedFlag,
              value: {
                fieldId: lastParams.pedItemId,
                fieldName: lastParams.pedItemName,
                imageURL: lastParams.pedItemImageURL,
                systemField: lastParams.pedItemSystem
              },
            }
          },
          users: lastParams.users,
          dates: { 
            start: { use: Boolean(lastParams.dateStart), value: lastParams.dateStart ? formatToFilterDateInput(lastParams.dateStart) : '' }, 
            end: { use: Boolean(lastParams.dateEnd), value: lastParams.dateEnd ? formatToFilterDateInput(lastParams.dateEnd) : '' } 
          },
        },
      }));
    });
  }

  renderCountCell = ({ viewIndex }) => {
    return viewIndex + 1;
  }

  renderDate = ({ original: { updatedAt } }) => {
    if (!updatedAt) {
      return null;
    }

    try {
      const { locale, yearCharsCount } = this.props;
      const baseOptions =  new Intl.DateTimeFormat(locale).resolvedOptions();
      const yearConfig = {
        2: `${yearCharsCount}-digit`,
        4: 'numeric'
      };
      const yearFormat = yearConfig[yearCharsCount] || 'numeric';
      const formatter = new Intl.DateTimeFormat(locale, { ...baseOptions, year: yearFormat });
      const fullDate = `${formatter.format(new Date(updatedAt))} ${formatDate(updatedAt, { format: 'hh:mm:ss A' })}`;
  
      return fullDate;
    } catch (err) {
      console.error(err);
      return null;
    }
  }

  get columns() {
    const { columnsWidth = {} } = this.state;
    const baseColumnsConfig = [
      {
        resizable: false,
        Header: '', 
        Cell: this.renderCountCell,
        className: 'FieldAuditDataExport__count-field',
        minWidth: 40,
        width: 40,
        accessor: 'count-column'
      },
      { Header: 'Item Type', accessor: 'itemType', minWidth: 100, sortable: true, },
      { Header: 'Item Name', accessor: 'itemName', minWidth: 200, sortable: true, },
      { Header: 'Field Name', accessor: 'fieldName', minWidth: 200, sortable: true, },
      { Header: 'Old Value', accessor: 'oldValue', minWidth: 200, sortable: true, },
      { Header: 'New Value', accessor: 'newValue', minWidth: 200, sortable: true, },
      { Header: 'User', accessor: 'userName', minWidth: 150, sortable: true, },
      { 
        Header: 'Date and Time', 
        accessor: 'updatedAt', 
        minWidth: 150, 
        sortable: true,
        Cell: this.renderDate,
      },
    ].map((x) => {
      if (columnsWidth[x.accessor]) {
        x.minWidth = columnsWidth[x.accessor];
      }

      if (typeof x.Header === 'string' && x.Header) {
        x.Header = I18n.t(x.Header);
      }

      return x;
    });

    return baseColumnsConfig;
  }

  handleColumnResize = (columns) => {
    columns.forEach(({ id, value }) => {
      setColumnsWidth({ name: id, value: Math.round(value) });
    });
  }

  prepareFiltersParameters = (parameters) => {
    const { users, dates, updates } = parameters;

    return {
      users,
      dateStart: dates.start.use ? formatToFilterDateValue(dates.start.value) : null,
      dateEnd: dates.end.use ? formatToFilterDateValue(dates.end.value) : null,
      indFlag: updates.individual.use,
      indItemId: updates.individual.value?.fieldId,
      indItemSystem: convertBoolToNumber(updates.individual.value?.systemField),
      pedFlag: updates.pedigree.use,
      pedItemId: updates.pedigree.value?.fieldId,
      pedItemSystem: convertBoolToNumber(updates.pedigree.value?.systemField),
    };
  }

  loadData = (params) => {
    const requestParams = {
      ...this.state.loadMetadata,
      ...params,
    };

    requestParams.pagination.scroll = true;

    return getReport(
      {
        ...requestParams,
        ...this.prepareFiltersParameters(this.state.filtering),
      }
    ).then(({ items, totalItems, columnsWidth }) => {
      const shouldReset = requestParams.pagination.start === 0;
      let currentItems = shouldReset ? [] : this.state.items;

      this.setState((state) => ({ 
        ...state, 
        items: [...currentItems, ...items],
        totalItemsCount: totalItems, 
        columnsWidth: state.columnsWidth || columnsWidth
      }));
    }).catch((err) => {
      this.handleRunNewReportError();

      throw err;
    });;
  }

  mergeLoadMetadata = (metadata = {}) => {
    this.setState((state) => ({
      ...state,
      loadMetadata: {
        ...this.state.loadMetadata, 
        ...metadata,
      }
    }));
  }

  get actions() {
    return [
      {
        iconSrc: setupIcon,
        label: 'New Report',
        onClick: this.openNewReport
      },
      {
        iconSrc: 'gif/fields.gif',
        label: 'Edit Fields',
        onClick: this.openFieldsEditing
      },
      {
        iconSrc: 'gif/print.gif',
        label: this.state.printing ? 'Preparing' : 'Print',
        disabled: this.state.printing || !this.state.items.length,
        onClick: this.handlePrint,
      },
      {
        iconSrc: 'gif/ss_export.gif',
        label: 'Export',
        disabled: !this.state.items.length,
        onClick: this.openTableExport,
      },
    ].map((x) => ({ ...x, label: I18n.t(x.label) }));
  }

  setMessageProps = (props) => {
    this.setState((state) => ({
      ...state,
      messageProps: {
        ...state.messageProps,
        size: 'medium',
        confirmText: 'Ok',
        cancelText: '',
        size: 'small',
        ...props,
      },
    }));
  }

  handlePrint = async () => {
    this.setState((x) => ({ ...x, printing: true }));
    const headers = this.columns.slice();
    
    const emptyRowCell = 0;
    const indexRowCell = 1;
    const internalIndexKey = 'internalIndexKey';

    headers.splice(emptyRowCell, indexRowCell, { Header: " ", accessor: internalIndexKey });

    const gridHeaderStyle = "border: 1px solid #ddd; padding: 14px; white-space: nowrap; font-size: 14px; font-family: sans-serif, serif, system-ui;";
    const gridStyle = "border: 1px solid #ddd; padding: 2px 4px; white-space: nowrap; font-size: 12px font-family: sans-serif, serif, system-ui;";

    function normalizeItemValue(item) {
      return Object.keys(item).reduce((acc, key) => {
        acc[key] = isNil(item[key]) ? "" : item[key];
        return acc;
      }, {});
    }

    print({
      printable: this.state.items.map((item, index) => ({ ...normalizeItemValue(item), [internalIndexKey]: ++index })),
      properties: headers.map((i) => ({
        field: i.accessor,
        displayName: i.Header
      })),
      type: "json",
      gridHeaderStyle,
      gridStyle
    });

    this.setState((x) => ({ ...x, printing: false }));
  }

  closeMessage = () => {
    this.setMessageProps({ show: false });
  }

  openNewReport = () => {
    this.setState((state) => ({ ...state, newReportConfigOpened: true }));
  }

  closeNewReport = () => {
    this.setState((state) => ({ ...state, newReportConfigOpened: false }));
  }

  openTableExport = () => {
    this.setState((state) => ({ ...state, dataExportOpened: true }));
  }

  closeTableExport = () => {
    this.setState((state) => ({ ...state, dataExportOpened: false }));
  }

  openFieldsEditing = () => {
    this.setState((state) => ({ ...state, fieldsEditingOpened: true }));
  }

  closeFieldsEditing = () => {
    this.setState((state) => ({ ...state, fieldsEditingOpened: false }));
  }
  
  handleRunNewReport = async (params) => {
    const nextLoadMetadataParams = {
      pagination: {
        start: 0,
        size: 45,
        scroll: true,
      },
      sorting: isEmpty(this.state.loadMetadata.sorting) 
        ? {}
        : this.state.loadMetadata.sorting
    };

    await getReport(
      {
        ...this.prepareFiltersParameters(params),
        ...nextLoadMetadataParams
      }
    ).then(({ items, totalItems }) => {
      scrollTop(this.rootRef.current);
      this.setState(
        (state) => ({ 
          ...state, 
          filtering: params,
          items, 
          totalItemsCount: totalItems, 
          loadMetadata: nextLoadMetadataParams 
        }),
        this.closeNewReport
      );
    }).catch((err) => {
      this.handleRunNewReportError();

      throw err;
    });
  }

  handleAuditingFieldsChange = async () => {
    const nextLoadMetadataParams = {
      pagination: {
        start: 0,
        size: 45,
        scroll: true,
      },
      sorting: isEmpty(this.state.loadMetadata.sorting) 
        ? {}
        : this.state.loadMetadata.sorting
    };

    await getReport(
      {
        ...this.prepareFiltersParameters(this.state.filtering),
        ...nextLoadMetadataParams
      }
    ).then(({ items, totalItems }) => {
      scrollTop(this.rootRef.current);
      this.setState(
        (state) => ({ 
          ...state, 
          items, 
          totalItemsCount: totalItems, 
          loadMetadata: nextLoadMetadataParams 
        }),
      );
      this.closeFieldsEditing();
    }).catch((err) => {
      this.handleRunNewReportError();

      throw err;
    });
  }

  getTheadThProps = (tableData, rowData, { id }) => getTestAttributes(MODULE_NAME, 'field', 'table-header-cell', id);

  getTdProps = (tableData, { index }, { id }) => getTestAttributes(MODULE_NAME, 'field', 'table-cell', `${index}-${id}`);

  getTrProps = (tableData, { index }) => getTestAttributes(MODULE_NAME, 'field', 'table-row', index);

  handleExportData = async (data) => {
    try {
      const { entity, headers } = await exportData(data);

      const filename = headers['content-disposition'].split('= ')[1];

      downloadFile(entity, filename, headers['content-type']);

      this.closeTableExport();
    } catch (err) {
      this.setMessageProps({
        header: 'Error',
        text: err?.message || I18n.t('Failed to export data'),
        show: true,
        onCustomOk: this.closeMessage,
      });
      console.error(err);
    }
  }

  handleRunNewReportError = () => {
    this.setMessageProps({
      header: 'Error',
      text: I18n.t('Failed to run new report'),
      show: true,
      onCustomOk: this.closeMessage,
    });
  }

  showMessage = (config) => {
    this.setMessageProps({
      ...config,
      show: true,
      onCustomOk: this.closeMessage,
    });
  }

  render() {
    const { items, totalItemsCount, newReportConfigOpened, fieldsEditingOpened, filtering, defaultFiltersInitialized, dataExportOpened, loadMetadata } = this.state;

    return (
      <div className="FieldAuditDataExport" ref={this.rootRef}>
        <MessageBox
          hideMessageBox={this.closeMessage}
          {...this.state.messageProps}
        />
        {dataExportOpened && (
          <DataExport 
            close={this.closeTableExport}
            onExport={this.handleExportData}
          />
        )}
        {newReportConfigOpened && (
          <NewReport 
            defaultSelectedUsers={filtering.users}
            defaultDates={filtering.dates}
            defaultUpdates={filtering.updates}
            onClose={this.closeNewReport}
            onSubmit={this.handleRunNewReport}
            showMessage={this.showMessage}
          />
        )}
        {fieldsEditingOpened && (
          <FieldsEditing 
            close={this.closeFieldsEditing}
            onExport={this.handleExportData}
            showMessage={this.showMessage}
            onSuccessSubmit={this.handleAuditingFieldsChange}
          />
        )}
        <Flex alignItems="center" className="FieldAuditDataExport__actions">
          {this.actions.map(({ iconSrc, label, ...btnProps }) => (
            <Button 
              key={label}
              className="FieldAuditDataExport__actions__btn" 
              {...getTestAttributes(MODULE_NAME, 'action', 'button', label.toLowerCase().replace(' ', '-'))} 
              {...btnProps}
            >
              {<img src={iconSrc} />}
              {I18n.t(label)}
            </Button>
          ))}
        </Flex>
        {defaultFiltersInitialized && (
          <Table
            infiniteScroll
            idProperty="transid"
            className="FieldAuditDataExport__table"
            config={{
              data: items,
              columns: this.columns,
              getTheadThProps: this.getTheadThProps,
              getTdProps: this.getTdProps,
              getTrProps: this.getTrProps,
              onResizedChange: debounce(this.handleColumnResize, 500)
            }}
            loadMetadata={loadMetadata}
            itemsCount={totalItemsCount}
            onLoadData={this.loadData}
            setLoadMetadata={this.mergeLoadMetadata}
          />
        )}
      </div>
    );
  }
}